import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, ButtonTypeMap } from '../ui/Button';
import { fontWeight, headerButtonStyles } from '../../styled/constants';
import Icon from '../Icon';
import { Typography } from '../ui/Typography';
import UserIcon from '../../public/static/images/item/user.svg';
import ArrowIcon from '../../public/static/images/item/arrow-right.svg';
import useToggle from '../../hooks/useToggle';
import { useProfileLogout } from '../../hooks/useProfileLogOut';
import { makeUrlWithLang } from '../../utils/helpers';
import config from '../../config';
import ButtonPlaceholder from '../Placeholders/ButtonPlaceholder';
import {
  DropdownItem,
  DropdownItemsContainer,
  SelectedButton,
  SelectedButtonArrowIcon,
  SelectedButtonUserIcon,
  DropDownProfileLink
} from './styled';

const LoginButton = ({ isAuth = false, isLoadingProfile }) => {
  const [isShowDropdown, toggleDropdown] = useToggle(false);
  const router = useRouter();
  const { handleLogout } = useProfileLogout();
  const currentPath = router.asPath.split('#')[0];
  const {
    t,
    i18n: { language: lang }
  } = useTranslation();
  const dropDownRef = useRef(null);
  const { protocol, host } = config;
  const hostString = path => makeUrlWithLang({ protocol, host, path, lang });

  const handleProfileClick = () => {
    router.push(`${currentPath}#authentication`, undefined, {
      shallow: true
    });
  };

  const dropdownItemsList = [
    {
      title: (
        <DropDownProfileLink
          href={hostString('/profile')}
          data-cy="header-profile-link"
          data-stat="header-profile-link"
        >
          {t('common.cabinet')}
        </DropDownProfileLink>
      ),
      onClick: null
    },
    {
      title: t('common.logout'),
      onClick: handleLogout
    }
  ];

  const handleDropDownOutsideClick = event => {
    if (
      dropDownRef.current &&
      !dropDownRef.current.contains(event.target) &&
      isShowDropdown
    ) {
      toggleDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleDropDownOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleDropDownOutsideClick);
    };
  }, [isShowDropdown]);

  if (isLoadingProfile) return <ButtonPlaceholder />;

  return (
    <>
      {isAuth ? (
        <SelectedButton
          ref={dropDownRef}
          onClick={toggleDropdown}
          onKeyPress={toggleDropdown}
          role="button"
          tabIndex="-1"
        >
          <SelectedButtonUserIcon>
            <Icon IconComponent={UserIcon} width={20} height={20} indent />
          </SelectedButtonUserIcon>
          <SelectedButtonArrowIcon>
            <Icon
              indent={false}
              IconComponent={ArrowIcon}
              width={8}
              height={14}
            />
          </SelectedButtonArrowIcon>
          {isShowDropdown && (
            <DropdownItemsContainer>
              {dropdownItemsList.map(({ title, onClick }, index) => (
                <React.Fragment key={index}>
                  <DropdownItem
                    onClick={onClick}
                    onKeyPress={onClick}
                    role="button"
                    tabIndex={index}
                  >
                    <Typography
                      tag="caption_2"
                      styles={{ fontWeight: fontWeight.thin, fontSize: '15px' }}
                    >
                      {title}
                    </Typography>
                  </DropdownItem>
                </React.Fragment>
              ))}
            </DropdownItemsContainer>
          )}
        </SelectedButton>
      ) : (
        <Button
          text={t('common.login')}
          onClick={handleProfileClick}
          state={ButtonTypeMap.singin}
          cypress="header-profile-btn"
          stat="header-profile-btn"
          hover="background-color: #eee"
          styles={headerButtonStyles}
        />
      )}
    </>
  );
};

LoginButton.propTypes = {
  isAuth: PropTypes.bool,
  isLoadingProfile: PropTypes.bool
};

export default LoginButton;
